import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Copy, Check, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "norbu_welcome_seen_v1";
const DELAY_MS = 2500;

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setOpen(false);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText("NORBU10");
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="welcome-popup-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md bg-[#F8F6F0] grain overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="welcome-popup"
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="welcome-popup-close"
              className="absolute top-4 right-4 z-10 text-[#163A2F]/60 hover:text-[#163A2F] transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>

            {/* Top band */}
            <div className="bg-[#163A2F] text-[#F8F6F0] grain relative px-8 pt-10 pb-8 text-center">
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.4 }}
                className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#C9A227]/60 text-[#C9A227] mb-4"
              >
                <Sparkles className="w-5 h-5" strokeWidth={1.4} />
              </motion.div>
              <div className="overline text-[#C9A227]">Welcome to Norbu</div>
              <h3 className="mt-3 text-3xl md:text-4xl font-extralight tracking-tight">
                10% off your <span className="italic text-[#C9A227]">first jacket.</span>
              </h3>
              <p className="mt-3 text-sm md:text-[15px] text-[#F8F6F0]/75 font-light leading-relaxed max-w-xs mx-auto">
                A little welcome gift from our Delhi studio. Use the code below at checkout.
              </p>
            </div>

            {/* Code card */}
            <div className="px-8 py-7 text-center">
              <div className="overline text-[#163A2F]/60 mb-3">Your code</div>
              <button
                onClick={copyCode}
                data-testid="welcome-copy-code"
                className="group w-full border-2 border-dashed border-[#C9A227]/60 bg-[#C9A227]/5 py-5 px-4 flex items-center justify-center gap-3 hover:bg-[#C9A227]/10 transition-colors"
                aria-label="Copy discount code"
              >
                <span className="text-2xl md:text-3xl font-light tracking-[0.3em] text-[#163A2F]">NORBU10</span>
                {copied
                  ? <Check className="w-5 h-5 text-[#163A2F]" strokeWidth={1.6} />
                  : <Copy className="w-4 h-4 text-[#163A2F]/60 group-hover:text-[#163A2F]" strokeWidth={1.6} />
                }
              </button>
              <div className="mt-2 text-xs text-[#163A2F]/60 font-light h-4">
                {copied ? "Copied to clipboard" : "Click to copy"}
              </div>

              <Link
                to="/categories"
                onClick={close}
                data-testid="welcome-popup-shop"
                className="mt-5 inline-block w-full bg-[#163A2F] text-white py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#0A1C16] transition-colors"
              >
                Shop the collection
              </Link>
              <button
                onClick={close}
                data-testid="welcome-popup-dismiss"
                className="mt-3 text-xs text-[#163A2F]/50 hover:text-[#163A2F] font-light tracking-wider underline-offset-4 hover:underline"
              >
                No thanks, I&apos;ll browse first
              </button>

              <div className="mt-5 text-[10px] text-[#163A2F]/45 font-light tracking-wider leading-relaxed">
                Valid on your first order · One use per customer
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
